import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { PackageCard } from "@/components/PackageCard";
import { Button } from "@/components/ui/button";
import { Sparkle, BookOpen, Confetti, GraduationCap, HandHeart, Rocket, VideoCamera, Clock, Compass } from "@phosphor-icons/react";

export default function Home() {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const featured = packages.filter((p) => p.featured).slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden dot-grid">
        <img
          src="/treehouse.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -left-16 bottom-0 h-[500px] w-auto opacity-[0.28] hidden md:block"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-sun/60 text-ink font-semibold text-sm"
            >
              <Sparkle size={16} weight="fill" className="text-brand-coral" /> Pre-K to 5th Grade Reading
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]"
            >
              Where little readers <span className="text-brand-sky">learn to soar</span>.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-5 font-body text-lg text-ink-soft leading-relaxed max-w-lg"
            >
              Playful, level-by-level reading packages you buy once and enjoy online forever — from first letter sounds all the way to fifth-grade comprehension.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/catalog" data-testid="hero-browse-btn">
                <Button className="rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold text-lg px-8 py-6 transition-transform hover:-translate-y-1">
                  <BookOpen size={22} weight="duotone" className="mr-2" /> Browse packages
                </Button>
              </Link>
              <Link to="/quiz" data-testid="hero-quiz-btn">
                <Button variant="outline" className="rounded-full border-2 border-slate-900/10 font-semibold text-lg px-8 py-6 text-ink hover:bg-white transition-transform hover:-translate-y-1">
                  <Compass size={22} weight="duotone" className="mr-2 text-brand-sky" /> Find my level
                </Button>
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
            className="relative"
          >
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-brand-coral/20 animate-float" />
            <div className="absolute -bottom-8 right-8 w-16 h-16 rounded-3xl bg-brand-sun/50 animate-float" style={{ animationDelay: "1s" }} />
            <img
              src="https://static.prod-images.emergentagent.com/jobs/289f5bfb-a361-4a61-8407-48b3421a1ec8/images/bfd8023c6e0d3c544a2379905b113596472c401d7abd710ea6bebef083e10b99.jpeg"
              alt="A happy Black girl reading a colorful picture book"
              className="relative rounded-[2rem] sticker w-full h-[420px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: GraduationCap, c: "#0ea5e9", t: "Pick a level", d: "From Pre-K phonics to 5th grade comprehension — choose what fits your child." },
            { icon: HandHeart, c: "#f43f5e", t: "Buy once", d: "One simple, secure payment. No subscriptions, no surprises." },
            { icon: Rocket, c: "#22c55e", t: "Read online", d: "Interactive lessons unlock instantly in your child's cozy bookshelf." },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white sticker rounded-3xl p-7">
              <span className="grid place-items-center w-12 h-12 rounded-2xl mb-4" style={{ backgroundColor: `${s.c}20` }}>
                <s.icon size={26} weight="duotone" style={{ color: s.c }} />
              </span>
              <h3 className="font-heading font-bold text-xl text-ink">{s.t}</h3>
              <p className="font-body text-ink-soft mt-2 leading-relaxed text-sm">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="font-body font-semibold text-brand-coral uppercase tracking-[0.2em] text-xs">Parent favorites</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink tracking-tight mt-1">Featured reading packages</h2>
          </div>
          <Link to="/catalog" className="hidden sm:block font-body font-semibold text-brand-sky hover:underline">View all →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
        </div>
      </section>

      {/* Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="relative overflow-hidden rounded-[2rem] bg-brand-sky p-10 sm:p-14">
          <Confetti size={140} weight="duotone" className="absolute -right-6 -top-6 text-white/15" />
          <div className="relative max-w-lg">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white tracking-tight">Reading confidence, one joyful lesson at a time.</h2>
            <p className="font-body text-white/90 mt-3 leading-relaxed">Start with a single package and watch your little reader light up.</p>
            <Link to="/catalog" data-testid="banner-cta">
              <Button className="mt-6 rounded-full bg-white text-brand-sky hover:bg-brand-sun hover:text-ink font-bold text-lg px-8 py-6 transition-transform hover:-translate-y-1">
                Explore the catalog
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 1-on-1 Tutoring */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white sticker rounded-[2rem] p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-grape/12 text-brand-grape font-semibold text-sm">
              <VideoCamera size={16} weight="duotone" /> Live 1-on-1 support
            </span>
            <h2 className="mt-4 font-heading font-bold text-3xl sm:text-4xl text-ink tracking-tight">Not sure where to start?</h2>
            <p className="mt-4 font-body text-ink-soft text-lg leading-relaxed">
              For <span className="font-semibold text-ink">ready learners who need a little direction</span>, book a one-hour online session with a caring reading coach. Every session <span className="font-semibold text-ink">starts with a consultation</span> so we can find the right fit together — nothing is final until we talk.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-5 font-body font-semibold text-ink-soft">
              <span className="flex items-center gap-2"><Clock size={20} weight="duotone" className="text-brand-grape" /> 1 hour</span>
              <span className="flex items-center gap-2"><VideoCamera size={20} weight="duotone" className="text-brand-grape" /> Online only</span>
              <span className="flex items-center gap-2"><Sparkle size={20} weight="fill" className="text-brand-grape" /> From $200/hr</span>
            </div>
            <Link to="/catalog/one-on-one-tutor-session" data-testid="home-tutor-cta">
              <Button className="mt-7 rounded-full bg-brand-grape hover:bg-brand-grape/90 text-white font-bold text-lg px-8 py-6 transition-transform hover:-translate-y-1">
                Request a consultation
              </Button>
            </Link>
            <p className="mt-3 font-body text-sm text-ink-soft italic">Consultation required · Results may vary.</p>
          </div>
          <div className="relative">
            <div className="absolute -top-5 -right-5 w-20 h-20 rounded-3xl bg-brand-grape/15 animate-float" />
            <img
              src="https://static.prod-images.emergentagent.com/jobs/289f5bfb-a361-4a61-8407-48b3421a1ec8/images/7cf80b01581b23e655117e8d3ff699f225c213a41bc878b2854633eeb6c15b97.jpeg"
              alt="A happy Black boy writing in a notebook at his desk"
              className="relative rounded-[1.5rem] sticker w-full h-[320px] object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
